import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "how-to-write-economics-essays-research-papers-guide",
  title: "How to Write Economics Essays & Research Papers: Structure, Citations & Academic Writing Guide",
  description: "Complete academic writing guide for economics undergraduates — Essay structure, arguments, citations, and critical evaluation.",
  category: "study-systems",
  authorSlug: "dhairya-madaan",
  pubDate: "2026-08-20",
  tags: ["academic-writing", "essays", "citations", "research"],
  readMinutes: 11,
  heroEyebrow: "Systems · Academic Writing",
};

export const Body = () => {
  return (
    <article className="prose lg:prose-xl">
      <h1>{meta.title}</h1>
      <p>Writing a great economics essay isn't just about dumping facts. It's about structuring an argument using economic theory and empirical evidence. Here is a complete guide.</p>
      
      <h2>Essay Structure</h2>
      <p><strong>Introduction:</strong> Start with a clear hook. Provide brief context. Most importantly, state your thesis statement—your main argument.</p>
      <p><strong>Body Paragraphs:</strong> Use the PEEL structure (Point, Evidence, Explanation, Link). Introduce economic models, use data, and explain the intuition behind the mechanisms.</p>
      <p><strong>Conclusion:</strong> Summarize the main points and restate the thesis. Discuss policy implications or limitations of your analysis.</p>

      <h2>Framing Economic Arguments</h2>
      <p>An argument in economics relies on theory and data. If you claim that a policy increases inflation, back it up with the Phillips Curve or Quantity Theory of Money, and provide empirical data.</p>
      
      <h2>Literature Review Methodology</h2>
      <p>Don't just list papers. Synthesize them. Identify consensus, debates, and gaps in the existing literature. "While Smith (2020) argues X, Jones (2021) finds Y, suggesting that..."</p>
      
      <h2>APA 7th Edition Citation Format</h2>
      <p>In-text citation: (Author, Year). Reference list: Author, A. A. (Year). Title of article. Title of Journal, volume(issue), page numbers. DOI.</p>
      
      <h2>Common Writing Mistakes in DU</h2>
      <p>Avoid descriptive writing without analysis. Don't use bullet points in formal essays unless specified. Ensure diagrams are explicitly referenced in the text (e.g., "As seen in Figure 1...").</p>
      
      <h2>Integrating Diagrams and Equations</h2>
      <p>Draw diagrams clearly, label axes properly (e.g., Price vs. Quantity). Always explain the shift in the text. For equations, define every variable immediately.</p>
      
      <h2>Critical Evaluation of Models</h2>
      <p>Always discuss the assumptions of economic models. Are they realistic? What happens if an assumption is relaxed? This shows critical thinking.</p>
      
      <h2>Sample Annotated Outline: Impact of GST on Indian Manufacturing</h2>
      <p>1. Introduction: Define GST, Thesis: GST formalized manufacturing but hurt MSMEs initially.</p>
      <p>2. Theoretical Framework: Deadweight loss reduction, cascading taxes.</p>
      <p>3. Empirical Evidence: Formalization vs. short-term disruption data.</p>
      <p>4. Policy Critique: Compliance costs for small firms.</p>
      <p>5. Conclusion: Long-term efficiency gains outweigh short-term costs.</p>
    </article>
  );
};


